import React from "react";
import { Button } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import {
  EventNote,
  EmojiObjects,
  Assignment,
  VolumeUp,
  FontDownload,
  Assessment,
  MenuBook,
  CalendarToday,
  EmojiEmotions,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import "./FeaturedTools.css";

const TOOLS = [
  {
    id: "calendar",
    title: "Calendar of Events",
    icon: CalendarToday,
    color: "#1769d6",
    soft: "#eef4ff",
    hover: "#dbeafe",
    description:
      "A clear view of key school activities and dates, helping teachers, students, and families plan throughout the year.",
  },
  {
    id: "activities",
    title: "Activities",
    icon: EmojiObjects,
    color: "#d97706",
    soft: "#fff7ed",
    hover: "#ffedd5",
    description:
      "Hands-on Montessori tasks chosen by age and development. The app records what each child does and how well they did it.",
  },
  {
    id: "assignments",
    title: "Assignments",
    icon: Assignment,
    color: "#2e7d32",
    soft: "#f0fdf4",
    hover: "#dcfce7",
    description:
      "Activities tailored to each child's level and interests. Parents see what's assigned, completed, and open for resubmission.",
  },
  {
    id: "alphabets",
    title: "Charts – Movable Alphabets",
    icon: FontDownload,
    color: "#7c4dff",
    soft: "#f5f3ff",
    hover: "#ede9fe",
    description:
      "Track words each child has learned with the Movable Alphabet and plan the next steps in language development.",
  },
  {
    id: "lessons",
    title: "Lesson Plans",
    icon: MenuBook,
    color: "#0d9488",
    soft: "#f0fdfa",
    hover: "#ccfbf1",
    description:
      "Pre-designed plans with objectives, materials, and procedures so teachers prepare and deliver with confidence.",
  },
  {
    id: "reports",
    title: "Reports",
    icon: Assessment,
    color: "#4338ca",
    soft: "#eef2ff",
    hover: "#e0e7ff",
    description:
      "Plan and track weekly progress across all areas of work—at each child's pace while meeting learning goals.",
  },
  {
    id: "sounds",
    title: "Charts – Sounds",
    icon: VolumeUp,
    color: "#db2777",
    soft: "#fdf2f8",
    hover: "#fce7f3",
    description:
      "Track phonetic letter sounds for early reading and writing, and plan what each student practices next.",
  },
  {
    id: "planner",
    title: "Activity Planner",
    icon: EventNote,
    color: "#0891b2",
    soft: "#ecfeff",
    hover: "#cffafe",
    description:
      "Weekly planning across language, math, practical life, and more—so every child works holistically at their own pace.",
  },
  {
    id: "stories",
    title: "Kids Own Stories",
    icon: EmojiEmotions,
    color: "#ea580c",
    soft: "#fff7ed",
    hover: "#fed7aa",
    description: null,
    bullets: [
      "AI-driven interest mapping & career matching",
      "Adaptive learning pathways & content suggestions",
      "Educator dashboards & progress analytics",
      "Parent engagement & real-time updates",
      "Cloud-based & scalable for institutions",
    ],
  },
];

const FeaturedTools = () => {
  const history = useHistory();

  return (
    <section className="featured-tools">
      <div className="featured-tools__inner">
        <header className="featured-tools__header">
          <p className="featured-tools__eyebrow">Platform capabilities</p>
          <h2 className="featured-tools__title">Featured Tools</h2>
          <p className="featured-tools__lead">
            A comprehensive suite built for Montessori education—crafted to enhance
            learning outcomes and streamline school operations.
          </p>
        </header>

        <div className="featured-tools__grid">
          {TOOLS.map(
            ({ id, title, icon: Icon, color, soft, hover, description, bullets }) => (
              <article
                key={id}
                className={`featured-tools__card featured-tools__card--${id}`}
                style={{
                  "--tool-accent": color,
                  "--tool-soft": soft,
                  "--tool-hover": hover,
                }}
              >
                <span className="featured-tools__card-accent" aria-hidden />
                <span className="featured-tools__icon" aria-hidden>
                  <Icon />
                </span>
                <h3 className="featured-tools__card-title">{title}</h3>
                {bullets ? (
                  <ul className="featured-tools__list">
                    {bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="featured-tools__card-text">{description}</p>
                )}
              </article>
            )
          )}
        </div>

        <aside className="featured-tools__cta">
          <div className="featured-tools__cta-glow" aria-hidden />
          <h3 className="featured-tools__cta-title">Ready to Transform Your School?</h3>
          <p className="featured-tools__cta-text">
            Join Montessori schools improving learning outcomes with Montestory.
          </p>
          <Button
            variant="contained"
            className="featured-tools__cta-btn"
            endIcon={<ArrowForwardIcon />}
            onClick={() => history.push("/montessori/schoolregistration")}
          >
            Start free trial
          </Button>
        </aside>
      </div>
    </section>
  );
};

export default FeaturedTools;
